import { Fragment } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/shared/lib/utils';

export interface Crumb {
  label: string;
  /** Ausente no último item — a página em que você está não é link. */
  to?: string;
}

interface BreadcrumbProps {
  items: readonly Crumb[];
  className?: string;
}

/**
 * Trilha da ABA a que a tela pertence, mais a própria tela:
 * `Projetos › Retiro de Jovens`, `Coordenações › Educação › Jovens`.
 *
 * ⚠️ **Ela já foi a hierarquia da igreja em toda tela, inclusive na de projeto,
 * e isso mudou em agosto/2026.** O argumento antigo era que a trilha ensina a
 * estrutura a quem nunca viu a hierarquia (PRD §2.1: sem treinamento) — o que
 * continua verdade, mas não valia o preço: um projeto alcançado pela tabela de
 * `/projetos` abria com `Coordenações › … ›` no topo, oferecendo "voltar" para
 * três telas por onde ninguém passou. Trilha que aponta para onde a pessoa não
 * esteve é caminho inventado, e ela é a única coisa na tela que a pessoa
 * pressupõe verdadeira sobre a própria navegação.
 *
 * A estrutura continua ensinada, em dois lugares onde ela é informação e não
 * percurso: os links de contexto abaixo do título do projeto (ministério ·
 * coordenação) e as telas de coordenação e ministério, que seguem com a trilha
 * da aba Coordenações porque ali ela é literalmente por onde se chega.
 *
 * A regra virou: **o primeiro item é sempre a aba dona da rota** — a mesma que
 * fica acesa na sidebar (ver `shared/constants/navigation.ts`). As duas coisas não podem
 * discordar; se discordarem, uma das duas está mentindo.
 *
 * No mobile a trilha rola horizontalmente em vez de quebrar em duas linhas:
 * nome de ministério é longo, e trilha quebrada empurra o título da página
 * para fora da primeira dobra.
 */
export function Breadcrumb({ items, className }: BreadcrumbProps) {
  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Trilha de navegação"
      className={cn('-mx-1 animate-fade-in overflow-x-auto px-1', className)}
    >
      <ol className="flex w-max items-center gap-1 text-sm text-muted-foreground">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <Fragment key={`${item.label}-${index}`}>
              <li className="flex items-center">
                {item.to && !isLast ? (
                  <Link
                    to={item.to}
                    className="rounded px-1.5 py-0.5 transition-colors hover:bg-accent hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span
                    className="px-1.5 py-0.5 font-medium text-foreground"
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {item.label}
                  </span>
                )}
              </li>
              {!isLast ? (
                <li aria-hidden="true" className="flex items-center">
                  <ChevronRight className="size-3.5 shrink-0 opacity-60" />
                </li>
              ) : null}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
